import React, { useState } from "react";
import { ArrowLeftOutlined, GiftOutlined } from "@ant-design/icons";
import { Alert, Button, Card, Divider, Input, Layout, Typography, message } from "antd";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { completeGiftCardSale, goToScreen, selectCheckoutTotals, selectPosFlow } from "../store/posSlice";
import axiosClient from "../api/axiosClient";

const { Content } = Layout;
const { Title, Text } = Typography;

function formatMoney(n: number) {
  return n.toLocaleString(undefined, { style: "currency", currency: "USD" });
}

function authHeader(): Record<string, string> {
  const token = window.localStorage.getItem("accessToken");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function GiftCardPaymentScreen() {
  const dispatch = useAppDispatch();
  const { error } = useAppSelector(selectPosFlow);
  const { total } = useAppSelector(selectCheckoutTotals);
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);
  const [balance, setBalance] = useState<number | null>(null);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState(false);

  async function handleCheckBalance() {
    setChecking(true);
    setLookupError(null);
    setBalance(null);
    try {
      const res = await axiosClient.get("/gift-cards/lookup", {
        params: { code },
        headers: authHeader(),
      });
      setBalance(res.data.current_balance);
    } catch (err: any) {
      setLookupError(err.response?.data || "Gift card not found or inactive.");
    } finally {
      setChecking(false);
    }
  }

  const canRedeem = balance !== null && balance >= total;

  async function handleRedeem() {
    setRedeeming(true);
    try {
      await dispatch(completeGiftCardSale({ code })).unwrap();
    } catch {
      message.error("Gift card redemption failed.");
    } finally {
      setRedeeming(false);
    }
  }

  return (
    <Layout style={{ minHeight: "100vh", background: "#f5f5f5" }}>
      <div style={{ padding: 14, background: "#fff" }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => dispatch(goToScreen("payment_method"))}>
          Back
        </Button>
      </div>
      <Divider style={{ margin: 0 }} />
      <Content style={{ padding: 24 }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ textAlign: "center", margin: "22px 0" }}>
            <Text type="secondary">Amount Due</Text>
            <Title style={{ marginTop: 6, fontSize: 64, lineHeight: 1.05 }}>{formatMoney(total)}</Title>
          </div>

          <div style={{ display: "flex", justifyContent: "center" }}>
            <Card style={{ width: "min(680px, 92vw)", borderRadius: 16 }}>
              <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                <div
                  style={{
                    width: 56,
                    height: 56,
                    borderRadius: 999,
                    background: "#fce7f3",
                    color: "#db2777",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 22,
                  }}
                >
                  <GiftOutlined />
                </div>
              </div>
              <Title level={4} style={{ textAlign: "center" }}>
                Gift Card Code
              </Title>
              <Input
                size="large"
                placeholder="XXXX-XXXX-XXXX"
                value={code}
                onChange={(e) => {
                  setCode(e.target.value.toUpperCase());
                  setBalance(null);
                  setLookupError(null);
                }}
                style={{ marginTop: 8 }}
              />
              <Button style={{ marginTop: 12, width: "100%" }} onClick={handleCheckBalance} loading={checking} disabled={!code}>
                Check Balance
              </Button>

              {balance !== null && (
                <Alert
                  style={{ marginTop: 16 }}
                  type={canRedeem ? "success" : "warning"}
                  showIcon
                  message={`Card Balance: ${formatMoney(balance)}`}
                  description={canRedeem ? "This gift card covers the full amount due." : "Insufficient balance to cover the full amount due."}
                />
              )}
              {lookupError && <Alert style={{ marginTop: 16 }} type="error" showIcon message={lookupError} />}
              {error && <Alert style={{ marginTop: 16 }} type="error" showIcon message={error} />}
            </Card>
          </div>

          <div style={{ marginTop: 18, display: "flex", justifyContent: "center", gap: 12 }}>
            <Button
              type="primary"
              size="large"
              loading={redeeming}
              disabled={!canRedeem}
              style={{ width: "min(640px, 70vw)", height: 56, borderRadius: 14, fontWeight: 700 }}
              onClick={handleRedeem}
            >
              Redeem Gift Card
            </Button>
            <Button
              size="large"
              style={{ width: 140, height: 56, borderRadius: 14, fontWeight: 600 }}
              onClick={() => dispatch(goToScreen("payment_method"))}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Content>
    </Layout>
  );
}
